import React, { useContext } from 'react';
import { useIntl } from 'react-intl';

import { FaMedal } from 'react-icons/fa6';

import { AwardsYear, Award } from 'interfaces';

import { AppLocale, BAND_ID, BAND_NAME, BAND_NAME_JP } from 'constant';
import { getSong, getAlbum } from 'utils';

import { LanguageContext } from 'context/languageContext';

import Title from 'components/ui/Title';
import ArrowLink from 'components/ui/ArrowLink';

import {
  Wrapper,
  AwardLink,
  AwardsTableSection,
  AwardsTable,
  AwardWrapper,
  StyledTitle,
  TheadTitle
} from './Awards.styles';

export interface AwardsProps {
  className?: string;
  awards: AwardsYear;
}

const Awards = ({ awards, className = '' }: AwardsProps) => {
  const intl = useIntl();
  const { locale } = useContext(LanguageContext);

  return (
    <Wrapper>
      <Title size="big" color="secondary">
        {intl.formatMessage({ id: 'awards.title' }, {
          bandName: locale === AppLocale.EN ? BAND_NAME : BAND_NAME_JP
        })}
      </Title>
      <p>
        {intl.formatMessage({ id: 'awards.description' }, {
          bandName: locale === AppLocale.EN ? BAND_NAME : BAND_NAME_JP
        })}
      </p>
      <AwardsTableSection>
        {Object.entries(awards).map(([year, entries]) => (
          <AwardsTable key={year}>
            <StyledTitle size="normal" color="primary">
              {intl.formatDate(new Date(year.toString()), {
                year: 'numeric'
              })}
            </StyledTitle>
            <table>
              <thead>
              <tr>
                <td>{intl.formatMessage({ id: 'awards.table.title.ceremony' })}</td>
                <td>{intl.formatMessage({ id: 'awards.table.title.category' })}</td>
                <td>{intl.formatMessage({ id: 'awards.table.title.nominee' })}</td>
                <td>{intl.formatMessage({ id: 'awards.table.title.result' })}</td>
              </tr>
              </thead>
              <tbody>
              {
                entries.map((award: Award) => (
                  <tr key={award.id}>
                    <td>
                      <TheadTitle>{intl.formatMessage({ id: 'awards.table.title.ceremony' })}</TheadTitle>
                      {intl.formatMessage({ id: `awards.table.ceremony.${award.ceremonyId}` })}
                    </td>
                    <td>
                      <TheadTitle>{intl.formatMessage({ id: 'awards.table.title.category' })}</TheadTitle>
                      {intl.formatMessage({ id: `awards.table.category.${award.categoryId}` })}
                    </td>
                    <td>
                      <TheadTitle>{intl.formatMessage({ id: 'awards.table.title.nominee' })}</TheadTitle>
                      {award.nominee === BAND_ID ? (
                        <AwardLink to='/about'>
                          {AppLocale.EN ? BAND_NAME : BAND_NAME_JP}
                        </AwardLink>
                      ) : award.nomineeType === 'album' ? (
                        <AwardLink to={getAlbum(award.nominee, false, locale, 'link')}>
                          {getAlbum(award.nominee, false, locale, 'name')}
                        </AwardLink>
                      ) : (
                        <AwardLink to={getSong(award.nominee, locale, 'link')}>
                          {getSong(award.nominee, locale, 'name')}
                        </AwardLink>
                      )}
                    </td>
                    <td>
                      <TheadTitle>{intl.formatMessage({ id: 'awards.table.title.result' })}</TheadTitle>
                      <AwardWrapper type={award.won ? 'won' : 'nominated'}>
                        <FaMedal /> {intl.formatMessage({ id: `awards.table.result.${award.won ? 'won' : 'nominated'}` })}
                      </AwardWrapper>
                    </td>
                  </tr>
                ))
              }
              </tbody>
            </table>
          </AwardsTable>
        ))}
      </AwardsTableSection>
      <ArrowLink size="normal" color="secondary" to="/about/history">
        {intl.formatMessage({ id: 'about.historyLink' }, {
          bandName: locale === AppLocale.EN ? BAND_NAME : BAND_NAME_JP
        })}
      </ArrowLink>
    </Wrapper>
  );
};

export default Awards;
